import { Link, NavLink } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/cn'
import { EnvBadge } from './EnvBadge'
import { featureNav } from '@/features/registry'
import type { NavItem } from '@/features/types'

// Nav is sourced from the feature registry — each slice contributes its own
// items (features/<name>/routes.tsx), so adding a link never touches this file.
// This array only fixes the GROUP ORDER; membership comes from the features.
const SECTION_ORDER: NavItem['section'][] = ['Overview', 'Sales', 'Tools', 'Admin']

export function Sidebar() {
  const { user, signOut } = useAuth()
  const isAdmin = user?.role === 'admin'

  const visible = featureNav.filter((i) => !i.adminOnly || isAdmin)
  const groups = SECTION_ORDER.map((section) => ({
    section,
    items: visible.filter((i) => i.section === section),
  })).filter((g) => g.items.length > 0)

  return (
    <nav className="flex h-full w-60 flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 pt-5 pb-4">
        <img
          src="/merged_logo_1.svg"
          alt="Penser · Innovative Claim Solutions · Arissa"
          className="w-full max-w-[200px]"
        />
        <div className="mt-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400">
          BD Portal
          <EnvBadge />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-3">
        {groups.map((group) => (
          <div key={group.section} className="mb-4">
            <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              {group.section}
            </div>
            {group.items.map((item) => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'mb-0.5 flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                      isActive
                        ? 'bg-penser-blue/10 font-medium text-penser-blue-dark'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                    )
                  }
                >
                  <Icon size={15} />
                  {item.label}
                </NavLink>
              )
            })}
          </div>
        ))}
      </div>

      {user && (
        <div className="border-t border-slate-100 px-3 py-3">
          <Link
            to="/profile"
            className="mb-2 flex items-center gap-2 rounded-md p-1 hover:bg-slate-50"
            title="View profile / change password"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-penser-blue/15 text-xs font-semibold text-penser-blue-dark">
              {user.firstName.slice(0, 1)}
              {user.lastName.slice(0, 1)}
            </div>
            <div className="min-w-0">
              <div className="truncate text-xs font-medium text-slate-700">
                {user.firstName} {user.lastName}
              </div>
              <div className="text-[10px] capitalize text-slate-400">{user.role.replace('_', ' ')}</div>
            </div>
          </Link>
          <button
            onClick={() => void signOut()}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      )}
    </nav>
  )
}
